import { useFormContext, useWatch } from 'react-hook-form';
import { MdInfoOutline } from 'react-icons/md';

import { Flex, Stack, Group, Text, Box, MantineColor } from '@ui';

import { AdaptiveFormRows } from './AdaptiveFormRows';
import classes from './styles/AdaptiveForm.module.css';
import {
  AdaptiveFormFieldConfigBase,
  AdaptiveFormNestableFieldConfig,
  SwitchFieldConfig
} from './types';

type AdaptiveNestedSwitchFormProps = {
  config: AdaptiveFormNestableFieldConfig;
  showFieldsByDefault?: boolean;
};

export const AdaptiveNestedSwitchForm = ({
  config,
  showFieldsByDefault
}: AdaptiveNestedSwitchFormProps) => {
  const { watch } = useFormContext();
  const value = watch(config.name!);

  const shouldRenderForm = () => {
    if (!config.fields?.length) return false;

    if (showFieldsByDefault) {
      return value !== false;
    }

    return !!value;
  };

  if (!shouldRenderForm()) return null;

  return (
    <Flex gap={0} direction="column" justify="center" wrap="nowrap">
      <AdaptiveFormRows config={config.fields} />
    </Flex>
  );
};

/**
 * Background colors for light and dark modes.
 * Both light and dark colors must be provided together.
 */
type Background = {
  /** Background color for light mode */
  light: MantineColor;
  /** Background color for dark mode */
  dark: MantineColor;
};

/**
 * Configuration options for card mode.
 */
type CardConfig = {
  /** Only show the card border/styling when the form is expanded (value is true) */
  showOnlyWhenExpanded?: boolean;
  /** Info tooltip text to display at the bottom of the card */
  infoToolTip?: string;
  /** Disable the large-screen bleed-out (105% width) — use when the card is already
   *  inside a padded container like a members accordion */
  contained?: boolean;
};

export type SwitchFormWrapperProps = {
  /** Field configuration including name, label, and nested fields */
  config: SwitchFieldConfig & AdaptiveFormFieldConfigBase;
  /** The switch question component to render */
  children?: React.ReactNode;
  /**
   * Card mode configuration.
   * - `true`: Always show card styling
   * - `{ showOnlyWhenExpanded: true }`: Only show card when expanded
   * - `{ infoToolTip: "text" }`: Show card with info tooltip
   * - `undefined`: No card styling (simple mode)
   */
  card?: boolean | CardConfig;
  /**
   * Custom background colors for the expanded state.
   * Must provide both light and dark colors together.
   * Uses light-dark() CSS mixin for automatic theme switching.
   */
  background?: Background;
  /**
   * Show nested fields by default even when value is not true.
   * Useful for forms that should display fields initially.
   */
  showFieldsByDefault?: boolean;
};

export const WithNestedFormFieldsForSwitch = ({
  children,
  config,
  card,
  background,
  showFieldsByDefault
}: SwitchFormWrapperProps) => {
  const value = useWatch({ name: config.name });

  // Parse card config
  const cardConfig = typeof card === 'object' ? card : card ? {} : null;
  const infoToolTip = cardConfig?.infoToolTip;

  // Determine if nested fields should be shown and styled as expanded
  // showFieldsByDefault=true: show when value !== false (show unless explicitly "No")
  // showFieldsByDefault=false: only show when value === true (explicit "Yes")
  const hasFields = !!config.fields;
  const isExpanded =
    hasFields && (showFieldsByDefault ? value !== false : value === true);

  // Card visibility (can be conditional based on expanded state)
  const showCard = card && (!cardConfig?.showOnlyWhenExpanded || isExpanded);

  // Parse background colors
  const hasCustomColors = !!background;
  const cssVars = hasCustomColors
    ? ({
        '--nested-switch-light-bg': background.light,
        '--nested-switch-dark-bg': background.dark
      } as React.CSSProperties)
    : undefined;

  // Simple mode: no card structure needed
  if (!card) {
    return (
      <Stack
        className={classes.nestedSwitchFormStack}
        style={{ ...config.wrapperStyle, ...cssVars }}
        mod={{ expanded: isExpanded, customColors: hasCustomColors }}
      >
        {children}
        <AdaptiveNestedSwitchForm
          config={config}
          showFieldsByDefault={showFieldsByDefault}
        />
      </Stack>
    );
  }

  // Full mode: with card structure
  return (
    <Stack
      className={classes.nestedSwitchFormStack}
      mod={{
        expanded: isExpanded,
        card: showCard,
        customColors: hasCustomColors,
        contained: cardConfig?.contained
      }}
      gap={showCard ? 0 : undefined}
      style={cssVars}
    >
      <Box
        className={classes.mainQuestionContainer}
        mod={{ card: showCard, expanded: isExpanded }}
      >
        {children}
      </Box>
      {config.fields && (
        <Box
          className={classes.nestedContentContainer}
          mod={{
            showContent: showCard && isExpanded
          }}
        >
          <AdaptiveNestedSwitchForm
            config={config}
            showFieldsByDefault={showFieldsByDefault}
          />
        </Box>
      )}
      {infoToolTip && (
        <Group
          gap={8}
          mod={{ card: showCard, expanded: isExpanded }}
          className={classes.infoContainer}
        >
          <MdInfoOutline className={classes.infoIcon} />
          <Text className={classes.infoToolTip}>{infoToolTip}</Text>
        </Group>
      )}
    </Stack>
  );
};
